import { Box, Grid, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { projectSlice } from '../../redux/ProjectSlice';
import { RootState } from '../../redux/Store';
import ProjectCard from './ProjectCard';
import { Project } from './projects';

const ProjectList = () => {
  const { projects, sortOrder } = useSelector((state: RootState) => state.project);
  const { selectedProjects } = useSelector((state: RootState) => state.project.projectSelect);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  const dispatch = useDispatch();

  const sortedProjects = useMemo(() => {
    const _projects = [...projects];

    return [..._projects].sort((a, b) => {
      switch (sortOrder) {
        case 'oldest':
          return new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
        case 'newest':
        default:
          return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
      }
    });
  }, [sortOrder, projects]);

  const handleProjectSelect = (index: number, isShiftKeyPressed: boolean) => {
    if (isShiftKeyPressed && lastSelectedIndex !== null) {
      const range = [Math.min(lastSelectedIndex, index), Math.max(lastSelectedIndex, index)];
      const projectsInRange = sortedProjects.slice(range[0], range[1] + 1);
      const newSelectedProjects = [...selectedProjects, ...projectsInRange];
      dispatch(projectSlice.actions.onSelectProjects(newSelectedProjects));
    } else {
      const clickedProject = sortedProjects[index];
      const newSelectedProjects = selectedProjects.some((p) => p.id === clickedProject.id)
        ? selectedProjects.filter((p) => p.id !== clickedProject.id)
        : [...selectedProjects, clickedProject];
      dispatch(projectSlice.actions.onSelectProjects(newSelectedProjects));
    }
    setLastSelectedIndex(index);
  };

  return (
    <Box>
      <Grid container spacing={3} sx={{ px: 2 }}>
        {sortedProjects?.map((project: Project, index: number) => (
          <Grid key={project.id} sx={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <ProjectCard
              project={project}
              handleProjectSelect={() => handleProjectSelect(index, false)}
              handleShiftProjectSelect={(e) => handleProjectSelect(index, e.shiftKey)}
              isSelected={selectedProjects?.some((p) => p.id === project.id)}
            />
          </Grid>
        ))}
      </Grid>
      {projects.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60%',
          }}
        >
          <Stack direction="column" spacing={2} useFlexGap flexWrap="wrap">
            <Typography variant="sectionTitle" sx={{ textAlign: 'center' }}>
              <Trans i18nKey="noProjects.body1">You have no projects!</Trans>
            </Typography>
            <Typography variant="body1" sx={{ wordWrap: 'break-word', fontWeight: 500 }}>
              <Trans i18nKey="noProjects.body2">Create your project by selecting the Create New button.</Trans>
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ProjectList;
